/**
 * <infinite-scroll-pagination>
 *
 * Wraps the standard pagination nav rendered by `snippets/pagination.liquid`, progressively
 * enhancing it into one of two JS-driven modes (set via the `mode` attribute):
 *
 * - "infinite_scroll": an always-present sentinel is watched with IntersectionObserver;
 *   scrolling it into view fetches and appends the next page automatically.
 * - "load_more": a visible button fetches and appends the next page on click.
 *
 * In both modes, once a next page is fetched, its product cards are appended to the
 * referenced list and `next-url` is updated so the process can keep going for as many
 * pages as are available.
 *
 * The plain <nav class="pagination"> markup stays in the DOM as a no-JS/SEO fallback and
 * is only hidden once this element has actually upgraded (see `[data-enhanced]` in CSS).
 */
class InfiniteScrollPagination extends HTMLElement {
  connectedCallback() {
    this.list = document.getElementById(this.getAttribute('list-id'));
    this.sectionId = this.getAttribute('section-id');
    this.mode = this.getAttribute('mode');
    this.spinner = this.querySelector('.infinite-scroll-pagination__spinner');
    this.sentinel = this.querySelector('.infinite-scroll-pagination__sentinel');
    this.button = this.querySelector('.infinite-scroll-pagination__load-more');

    // Only hide the fallback <nav> once we know one of the enhanced modes can actually
    // take over; otherwise leave it visible and functional.
    if (!this.list) return;
    if (this.mode === 'infinite_scroll' && !this.sentinel) return;
    if (this.mode === 'load_more' && !this.button) return;

    this.dataset.enhanced = '';
    this.loading = false;

    if (this.mode === 'load_more') {
      this.button.addEventListener('click', () => this.loadNextPage());
    } else if (this.mode === 'infinite_scroll') {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) this.loadNextPage();
      }, { rootMargin: '600px 0px' });

      this.observer.observe(this.sentinel);
    }

    this.updateButtonVisibility();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  announce() {
    const announcement = this.getAttribute('announcement');
    const region = document.getElementById('status-announcement');

    if (announcement && region) region.textContent = announcement;
  }

  updateButtonVisibility() {
    if (this.button) this.button.hidden = !this.hasAttribute('next-url');
  }

  async loadNextPage() {
    const nextUrl = this.getAttribute('next-url');

    if (!nextUrl || this.loading) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    if (this.button) this.button.hidden = true;

    try {
      // Fetch through Shopify's section-rendering endpoint (the same mechanism the theme's
      // own facet/sort AJAX uses) so only this section's markup comes back, instead of a
      // full page (header, footer, every other section) that's parsed just to discard.
      const fetchUrl = new URL(nextUrl, window.location.origin);
      if (this.sectionId) fetchUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(fetchUrl);

      if (!response.ok) throw new Error(`Failed to load ${nextUrl}: ${response.status}`);

      const html = await response.text();
      const newDocument = new DOMParser().parseFromString(html, 'text/html');
      const newList = newDocument.getElementById(this.list.id);

      if (newList) {
        const newNodes = [...newList.childNodes];

        // `[reveal-on-scroll="true"]` (product cards, promo blocks) starts at opacity: 0 and
        // is only ever animated to opacity: 1 by <product-list>'s one-time, initial-connect
        // IntersectionObserver — it never re-fires for nodes appended later, so cards fetched
        // here would otherwise stay permanently invisible. Stripping the attribute (the same
        // approach theme.js itself uses elsewhere to force-reveal content outside that flow)
        // shows them immediately instead.
        newNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          node.removeAttribute('reveal-on-scroll');
          node.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        });

        this.list.append(...newNodes);
        this.announce();
      }

      const newTrigger = newDocument.querySelector(
        `infinite-scroll-pagination[list-id="${this.getAttribute('list-id')}"]`
      );
      const followingUrl = newTrigger?.getAttribute('next-url');

      if (followingUrl) {
        this.setAttribute('next-url', followingUrl);

        // Newly appended content may not have pushed the sentinel back out of the
        // observed region, in which case the observer won't naturally refire. Re-observing
        // forces a fresh check so a still-visible sentinel keeps loading pages.
        if (this.observer) {
          this.observer.unobserve(this.sentinel);
          this.observer.observe(this.sentinel);
        }
      } else {
        this.removeAttribute('next-url');
        this.observer?.disconnect();
      }

      history.replaceState(history.state, '', nextUrl);
    } catch (error) {
      console.error(error);
      // Keep `next-url` as is so the visible pagination link (and, in load_more mode, a
      // manual retry click) still work. In infinite_scroll mode, also retry automatically
      // shortly, in case this was a transient network error and the sentinel is still in view.
      if (this.mode === 'infinite_scroll') {
        setTimeout(() => {
          if (this.isConnected) this.loadNextPage();
        }, 2000);
      }
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      this.updateButtonVisibility();
    }
  }
}

if (!customElements.get('infinite-scroll-pagination')) {
  customElements.define('infinite-scroll-pagination', InfiniteScrollPagination);
}
